import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  useDosimetros,
  useEquipment,
  useUser,
} from "@services/servicesGenericsTables";
import { useLaboratory } from "@services/servicesGenericsTables/useLaboratory";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useTypesUnits } from "@services/servicesGenericsTables/useTypesUnits";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import ResultsTable from "../commonForms/resultsTable";
import dayjs from "dayjs";
import { OPTIONS_A_B, OPTIONS_YES_NO } from "../iodo131/utils/constants";
import { convertToNumber } from "@app/utils/functions";
import { useSample } from "@app/pages/samples/utils/useSample";
import { useParams } from "react-router-dom";

const CalculationDP = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { idMuestra } = useParams();
  const { equipmentList } = useEquipment();
  const { laboratoriesList } = useLaboratory();
  const { currentCalculation, saveCalculate, loadingResults } = useAnalysis();
  const { get } = useSample();
  const { typesUnitsis } = useTypesUnits();
  const filteredCalculation = useFilteredProcedures("CAL");
  const { dosimetrosList } = useDosimetros();
  const groupValue = form.watch("group");
  const codeAnalysis = form.getValues("typeAnalysis")?.codigo;

  const filteredDosimetros = useMemo(() => {
    const codeInstallation = get(Number(idMuestra))?.installation?.codigo;
    return groupValue
      ? dosimetrosList.filter(
          (dosimetro) =>
            dosimetro.grupo === groupValue.value &&
            dosimetro.instalacion === codeInstallation
        )
      : [];
  }, [dosimetrosList, groupValue]);

  const handleCalculate = async () => {
    await saveCalculate(codeAnalysis as string);
  };

  const textButtonCalculation = "Calcular";

  const calcularDesviacionEstandar = (valores) => {
    const valoresFiltrados = valores.filter(
      (val) => val !== null && val !== undefined && val !== ""
    );
    if (valoresFiltrados.length < 2) return 0; // STDEV.S requiere al menos 2 valores
    const media =
      valoresFiltrados.reduce((a, b) => a + b, 0) / valoresFiltrados.length;
    const varianza =
      valoresFiltrados.reduce(
        (sum, valor) => sum + Math.pow(valor - media, 2),
        0
      ) /
      (valoresFiltrados.length - 1);
    return Math.sqrt(varianza);
  };

  const lectura1 = convertToNumber(form.watch("lectura_1_usv")) || 0;
  const lectura2 = convertToNumber(form.watch("lectura_2_usv")) || 0;
  const lectura3 = convertToNumber(form.watch("lectura_3_usv")) || 0;
  const lectura4 = convertToNumber(form.watch("lectura_4_usv")) || 0;

  const valorMediaLecturas = useMemo(() => {
    const lecturas = [lectura1, lectura2, lectura3, lectura4].filter(
      (val) => val !== 0
    );
    if (lecturas.length === 0) return 0;
    return (lecturas.reduce((a, b) => a + b, 0) / lecturas.length).toFixed(2);
  }, [lectura1, lectura2, lectura3, lectura4]);

  const errorLecturas = useMemo(() => {
    const lecturas = [lectura1, lectura2, lectura3, lectura4].filter(
      (val) => val !== 0
    );
    return calcularDesviacionEstandar(lecturas).toFixed(2);
  }, [lectura1, lectura2, lectura3, lectura4]);

  const startSamplingDate = form.watch("start_date_time");
  const endSamplingDate = form.watch("end_date_time");
  const deleteDate = form.watch("calculation_delete_date");
  const readDate = form.watch("calculation_read_date");

  const transitDays = useMemo(() => {
    const inicio = dayjs(startSamplingDate).startOf("day");
    const borrado = dayjs(deleteDate).startOf("day");
    const lectura = dayjs(readDate).startOf("day");
    const fin = dayjs(endSamplingDate).startOf("day");
    const dias1 = inicio.diff(borrado, "day");
    const dias2 = lectura.diff(fin, "day");

    return dias1 + dias2;
  }, [startSamplingDate, endSamplingDate, deleteDate, readDate]);

  const expositionDays = useMemo(() => {
    const inicio = dayjs(startSamplingDate).startOf("day");
    const fin = dayjs(endSamplingDate).startOf("day");
    return fin.diff(inicio, "day");
  }, [startSamplingDate, endSamplingDate]);

  useEffect(() => {
    form.setValue("valor_media_lecturas_usv", valorMediaLecturas);
    form.setValue("error_lecturas_usv", errorLecturas);
    form.setValue("transit_days", transitDays);
    form.setValue("exposition_days", expositionDays);
  }, [valorMediaLecturas, errorLecturas, transitDays, expositionDays, form]);

  return (
    <>
      {currentCalculation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key="calculation_analyst"
              name="calculation_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MDatePicker
              name="calculation_delete_date"
              pickerType="datetime"
              label="Fecha de borrado"
              disabled
            />
            <MDatePicker
              name="calculation_read_date"
              pickerType="datetime"
              label="Fecha y hora de lectura"
              disabled
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipo de Contaje"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              multiple={true}
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={6}>
            <MAutocomplete
              key={JSON.stringify(form.getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              disabled
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("is_delete"))}
              name="is_delete"
              options={OPTIONS_YES_NO}
              label="Borrado"
              getOptionLabel={(option) => `${option.value}` || ""}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("is_read"))}
              name="is_read"
              options={OPTIONS_YES_NO}
              label="Lectura"
              getOptionLabel={(option) => `${option.value}` || ""}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("group"))}
              name="group"
              options={OPTIONS_A_B}
              label="Grupo"
              getOptionLabel={(option) => `${option.label}` || ""}
            />

            <MAutocomplete
              name="dosimetro"
              options={filteredDosimetros}
              label="Nº Dosímetro"
              getOptionLabel={(option) => `${option.numero}` || ""}
            />

            <MTextField
              name="lectura_1_usv"
              label="Lectura 1 (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
              validation={{ required: "El campo es requerido" }}
              fullWidth
            />

            <MTextField
              name="lectura_2_usv"
              label="Lectura 2 (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
              fullWidth
            />

            <MTextField
              name="lectura_3_usv"
              label="Lectura 3 (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
            />
            <MTextField
              name="lectura_4_usv"
              label="Lectura 4 (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
            />

            <MTextField
              name="valor_media_lecturas_usv"
              label="Valor media Lecturas (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
              disabled
            />

            <MTextField
              name="error_lecturas_usv"
              label="Error Lecturas (µSv)"
              type="number"
              inputProps={{ step: "0.1" }}
              disabled
            />

            <MTextField
              name="transit_days"
              label="Días tránsito"
              type="number"
              disabled
            />

            <MTextField
              name="exposition_days"
              label="Días exposición"
              type="number"
              disabled
            />

            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(form.watch("calculation_counting_date"))}
            />
          </ResponsiveFormContainer>

          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!form.watch("idResultPreload")}
              />
            </Box>
          )}

          <ResultsTable />
        </>
      )}
    </>
  );
};

export default CalculationDP;
